import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import ISupplierModel from "../../../../models/ISupplierModel";
import OrderStatesEnum from "../../../../models/OrderStatesEnum";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import { getStepChipSpecs, IWorkflowParts, IWorkflowStateOption, vendorKey, WORKFLOW_STATE_OPTIONS } from "./workflowSteps";

type ChipsProps = { step: IOrder_Progress_State; supplierModel: ISupplierModel };

/** The chips summarizing one step: what it emails, locks and triggers. */
export const WorkflowStepChips = (props: ChipsProps) => {
    const intl = useIntl();
    const t = (key: string, keepVendorWord?: boolean) => {
        const id = keepVendorWord ? key : vendorKey(key, props.supplierModel);

        return intl.formatMessage({ id, defaultMessage: `_${id}_` });
    };

    return (
        <span className={"WorkflowStep-chips"}>
            {getStepChipSpecs(props.step, props.supplierModel).map((chip) => {
                const text = chip.names
                    ? intl.formatMessage(
                          { id: chip.labelKey, defaultMessage: `_${chip.labelKey}_` },
                          { recipients: chip.names.map((name) => t(name)).join(", ") }
                      )
                    : t(chip.labelKey, chip.keepVendorWord);

                return (
                    <span
                        key={chip.id}
                        className={`SettingsChip ${chip.tone}`}
                        data-testid={`chip-${props.step.order_state_id}-${chip.id}`}>
                        {chip.icon ? <i className={`fad ${chip.icon}`} aria-hidden={true} /> : null}
                        {text}
                    </span>
                );
            })}
        </span>
    );
};

type RowProps = {
    step: IOrder_Progress_State;
    position: number;
    supplierModel: ISupplierModel;
    selected: boolean;
    locked?: boolean;
    lockedTitleKey?: string;
    edited?: boolean;
    onSelect: (id: OrderStatesEnum) => void;
};

const StepRowBody = (props: RowProps) => {
    return (
        <>
            <span className={"WorkflowStep-num"}>{String(props.position).padStart(2, "0")}</span>
            <span className={"WorkflowStep-name"}>
                <OwLabel title={props.step.name} noBottomMargin={true} />
                {props.selected ? (
                    <span className={"SettingsChip brand"}>
                        <OwLabel title={"editing"} noBottomMargin={true} />
                    </span>
                ) : null}
                {props.edited ? (
                    <span className={"SettingsChip brand"} data-testid={`wf-step-${props.step.order_state_id}-edited`}>
                        <OwLabel title={"edited"} noBottomMargin={true} />
                    </span>
                ) : null}
            </span>
            <WorkflowStepChips step={props.step} supplierModel={props.supplierModel} />
        </>
    );
};

const SelectButton = (props: RowProps) => (
    <button
        type={"button"}
        className={"WorkflowStep-select"}
        onClick={() => props.onSelect(props.step.order_state_id)}
        aria-current={props.selected}
        data-testid={`wf-select-${props.step.order_state_id}`}>
        <StepRowBody {...props} />
    </button>
);

/** A step the user can drag: the grip drags, the rest of the row selects. */
const SortableStepRow = (props: RowProps) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props.step.order_state_id,
    });

    // Translate only: the default transform also scales the row to the one it hovers over, which squashes the chips.
    const style = { transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={"WorkflowStep" + (props.selected ? " selected" : "")}
            role={"listitem"}
            data-testid={`wf-step-${props.step.order_state_id}`}>
            <span
                ref={setActivatorNodeRef}
                className={"WorkflowStep-grip"}
                {...attributes}
                {...listeners}
                data-testid={`wf-grip-${props.step.order_state_id}`}>
                <i className={"fad fa-grip-vertical"} aria-hidden={true} />
            </span>
            <SelectButton {...props} />
        </div>
    );
};

const LockedStepRow = (props: RowProps) => {
    const intl = useIntl();

    return (
        <div
            className={"WorkflowStep locked" + (props.selected ? " selected" : "")}
            role={"listitem"}
            data-testid={`wf-step-${props.step.order_state_id}`}>
            <i
                className={"fad fa-lock WorkflowStep-lock"}
                aria-hidden={true}
                title={
                    props.lockedTitleKey
                        ? intl.formatMessage({ id: props.lockedTitleKey, defaultMessage: `_${props.lockedTitleKey}_` })
                        : undefined
                }
            />
            <SelectButton {...props} />
        </div>
    );
};

type AddMenuProps = {
    taken: Set<OrderStatesEnum>;
    onAdd: (option: IWorkflowStateOption) => void;
    onClose: () => void;
};

/** The Add step picker: every usable state, searchable, with the ones already placed shown but not addable. */
const AddStepMenu = (props: AddMenuProps) => {
    const intl = useIntl();
    const [query, setQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const t = (id: string) => intl.formatMessage({ id, defaultMessage: `_${id}_` });

    useEffect(() => {
        inputRef.current?.focus();

        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                props.onClose();
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                props.onClose();
            }
        };

        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);

        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const options = useMemo(() => {
        const needle = query.trim().toLowerCase();

        return WORKFLOW_STATE_OPTIONS.map((option) => ({ option, label: t(option.name), group: t(option.groupKey) })).filter(
            ({ label }) => !needle || label.toLowerCase().includes(needle)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, intl]);

    const available = options.filter(({ option }) => !props.taken.has(option.order_state_id));
    const taken = options.filter(({ option }) => props.taken.has(option.order_state_id));

    // One sticky header per group, in the catalog's order, holding only the matches left in it.
    const groups = Array.from(new Set(available.map(({ option }) => option.groupKey))).map((groupKey) => ({
        groupKey,
        items: available.filter(({ option }) => option.groupKey === groupKey),
    }));

    return (
        <div className={"WorkflowAddMenu"} role={"dialog"} aria-label={t("add_step")} ref={rootRef} data-testid={"wf-add-menu"}>
            <label className={"WorkflowAddMenu-search"}>
                <i className={"fad fa-magnifying-glass"} aria-hidden={true} />
                <input
                    ref={inputRef}
                    type={"search"}
                    value={query}
                    placeholder={t("search_steps")}
                    aria-label={t("search_steps")}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid={"wf-add-search"}
                />
            </label>
            <div className={"WorkflowAddMenu-list"}>
                {groups.length ? (
                    groups.map(({ groupKey, items }) => (
                        <div key={groupKey} className={"WorkflowAddMenu-group"} data-testid={`wf-add-group-${groupKey}`}>
                            <div className={"WorkflowAddMenu-head"}>{t(groupKey)}</div>
                            {items.map(({ option, label }) => (
                                <button
                                    key={option.order_state_id}
                                    type={"button"}
                                    className={"WorkflowAddMenu-option"}
                                    onClick={() => props.onAdd(option)}
                                    data-testid={`wf-add-option-${option.order_state_id}`}>
                                    <i className={"fad fa-plus"} aria-hidden={true} />
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>
                    ))
                ) : (
                    <div className={"WorkflowAddMenu-empty"}>{t("no_steps_match")}</div>
                )}
                {taken.length ? (
                    <div className={"WorkflowAddMenu-group"}>
                        <div className={"WorkflowAddMenu-head"}>{t("already_in_workflow")}</div>
                        {taken.map(({ option, label }) => (
                            <div key={option.order_state_id} className={"WorkflowAddMenu-option taken"} aria-disabled={true}>
                                <i className={"fad fa-check"} aria-hidden={true} />
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
            <div className={"WorkflowAddMenu-foot"}>{t("new_step_lands_after_selected")}</div>
        </div>
    );
};

type Props = {
    parts: IWorkflowParts;
    allSteps: IOrder_Progress_State[];
    supplierModel: ISupplierModel;
    selectedId: OrderStatesEnum | null;
    onSelect: (id: OrderStatesEnum) => void;
    onReorder: (fromIndex: number, toIndex: number) => void;
    onAdd: (option: IWorkflowStateOption) => void;
    isStepEdited: (step: IOrder_Progress_State) => boolean;
};

/** The left column: Preliminary, the sortable steps, the locked fulfillment steps and the final slot. */
export const WorkflowStepList = (props: Props) => {
    const [adding, setAdding] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const editableIds = props.parts.editable.map((step) => step.order_state_id);
    const taken = useMemo(() => new Set(props.allSteps.map((step) => step.order_state_id)), [props.allSteps]);

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const from = editableIds.indexOf(Number(active.id));
        const to = editableIds.indexOf(Number(over.id));

        if (from >= 0 && to >= 0) {
            props.onReorder(from, to);
        }
    };

    let position = 0;
    const next = () => ++position;

    const rowProps = (step: IOrder_Progress_State) => ({
        step,
        position: next(),
        supplierModel: props.supplierModel,
        selected: props.selectedId === step.order_state_id,
        edited: props.isStepEdited(step),
        onSelect: props.onSelect,
    });

    return (
        <div className={"WorkflowStepList"} role={"list"} data-testid={"wf-step-list"}>
            <div className={"WorkflowStepList-head"}>
                <OwLabel title={"workflow_steps"} noBottomMargin={true} isBold={true} />
                <div className={"WorkflowStepList-add"}>
                    <OwButton
                        title={"add_step"}
                        className={"btn-outline-secondary"}
                        leftIconClass={"fad fa-plus"}
                        fn={() => setAdding(!adding)}
                        disableDblClickPrevention={true}
                        disableWidthOnClick={true}
                        testId={"wf-add-step"}
                    />
                    {adding ? (
                        <AddStepMenu
                            taken={taken}
                            onClose={() => setAdding(false)}
                            onAdd={(option) => {
                                setAdding(false);
                                props.onAdd(option);
                            }}
                        />
                    ) : null}
                </div>
            </div>

            {props.parts.preliminary ? <LockedStepRow {...rowProps(props.parts.preliminary)} lockedTitleKey={"always_first"} /> : null}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={editableIds} strategy={verticalListSortingStrategy}>
                    {props.parts.editable.map((step) => (
                        <SortableStepRow key={step.order_state_id} {...rowProps(step)} />
                    ))}
                </SortableContext>
            </DndContext>

            {props.parts.fulfillment.length ? (
                <>
                    <div className={"WorkflowStepList-group"}>
                        <i className={"fad fa-truck"} aria-hidden={true} />
                        <OwLabel title={"fulfillment_steps_from_shipping"} noBottomMargin={true} />
                    </div>
                    {props.parts.fulfillment.map((step) => (
                        <LockedStepRow key={step.order_state_id} {...rowProps(step)} lockedTitleKey={"order_set_by_shipping"} />
                    ))}
                </>
            ) : null}

            <div className={"WorkflowStepList-group"}>
                <i className={"fad fa-flag-checkered"} aria-hidden={true} />
                <OwLabel title={"final_step_optional"} noBottomMargin={true} />
            </div>
            {props.parts.final ? (
                <LockedStepRow {...rowProps(props.parts.final)} lockedTitleKey={"final_step_always_last"} />
            ) : (
                <div className={"WorkflowStepList-slot"} data-testid={"wf-no-final"}>
                    <OwLabel title={"no_final_step"} noBottomMargin={true} />
                    <OwLabel title={"help_text_no_final_step"} noBottomMargin={true} className={"text-muted"} />
                </div>
            )}
        </div>
    );
};

export default WorkflowStepList;
